<template>
  <div class="stall-orders">
    <!-- 顶部导航 -->
    <header class="header">
      <button class="btn-icon" @click="$router.push('/stall/home')">←</button>
      <div class="header-center">{{ $t('navigation.orders') }}</div>
      <div style="width: 24px;"></div>
    </header>

    <!-- 状态筛选 -->
    <div class="filter-tabs">
      <div 
        v-for="tab in statusTabs" 
        :key="tab.value"
        :class="['filter-tab', { active: currentStatus === tab.value }]"
        @click="currentStatus = tab.value"
      >
        {{ $t(tab.labelKey) }}（{{ getTabCount(tab.value) }}）
      </div>
    </div>

    <!-- 内容 -->
    <div class="content">
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>{{ $t('common.loading') }}</p>
      </div>

      <div v-else-if="orders.length === 0" class="empty-state">
        <div class="empty-icon">📋</div>
        <p>{{ $t('order.noOrder') }}</p>
        <button class="btn-primary" @click="$router.push('/stall/home')">{{ $t('cart.goShopping') }}</button>
      </div>

      <div v-else>
        <div v-for="order in orders" :key="order.id" class="order-card" @click="viewDetail(order)">
          <div class="order-header">
            <span class="order-no">#{{ order.orderNo }}</span>
            <span class="order-status" :class="getStatusClass(order.status)">{{ getStatusText(order.status) }}</span>
          </div>
          <div class="order-items">
            <div v-for="item in order.items" :key="item.id" class="order-item">
              <div class="item-image">
                <img v-if="item.productImage" :src="getImageUrl(item.productImage)" alt="" class="item-img" />
                <div v-else class="item-img-placeholder">📦</div>
              </div>
              <div class="item-info">
                <div class="item-name">{{ item.productName }}</div>
                <div class="item-qty">{{ item.quantity }} {{ item.unit || $t('common.unknown') }}</div>
              </div>
            </div>
          </div>
          <div class="order-footer">
            <span class="order-time">{{ formatTime(order.createdAt) }}</span>
            <span class="order-total">S$ {{ formatAmount(order.totalAmount) }}</span>
          </div>
          <div v-if="order.paidAmount > 0" class="order-paid">
            {{ $t('payment.paid') }} S$ {{ formatAmount(order.paidAmount) }}
          </div>
          <div v-if="order.pendingPaymentAmount > 0" class="order-pending-pay">
            ⏳ {{ $t('payment.paymentPending') }} S$ {{ formatAmount(order.pendingPaymentAmount) }}
          </div>
          <div class="order-actions" @click.stop>
            <button v-if="canPay(order)" class="btn-primary" @click="goToPay(order)">{{ $t('order.goToPay') }}</button>
            <button v-if="canCancel(order)" class="btn-danger" @click="cancelOrder(order)">{{ $t('common.cancel') }}</button>
            <button v-if="canAfterSales(order)" class="btn-warning" @click="goToOrderAfterSales(order)">{{ $t('afterSales.title') }}</button>
            <button class="btn-whatsapp" @click="contactWholesaler(order)" :disabled="!order.wholesalerWhatsapp">💬 {{ $t('order.contactWholesaler') }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部导航 -->
    <nav class="bottom-nav">
      <button class="nav-item" @click="navigateTo('products')">
        <span class="nav-item-icon">🏠</span>
        <span>{{ $t('navigation.home') }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('cart')">
        <span class="nav-item-icon">🛒</span>
        <span>{{ $t('navigation.cart') }}</span>
        <span v-if="cartCount > 0" class="cart-badge">{{ cartCount }}</span>
      </button>
      <button class="nav-item active" @click="navigateTo('orders')">
        <span class="nav-item-icon">📋</span>
        <span>{{ $t('navigation.orders') }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('profile')">
        <span class="nav-item-icon">👤</span>
        <span>{{ $t('navigation.profile') }}</span>
      </button>
    </nav>

    <!-- 申请售后弹窗 -->
    <el-dialog
      :title="'📝 ' + $t('afterSales.applyReturn') + ' / Apply After-Sales'"
      :visible.sync="showApplyDialog"
      width="90%"
      class="apply-dialog"
    >
      <div class="apply-info">
        📋 {{ $t('order.orderNo') }}: {{ currentOrder && currentOrder.orderNo }} · S$ {{ formatAmount(currentOrder && currentOrder.totalAmount) }}
      </div>

      <!-- 问题原因 -->
      <div class="form-section">
        <div class="form-label">{{ $t('afterSales.returnReason') }}</div>
        <div class="reason-tags">
          <span
            v-for="r in reasons"
            :key="r.value"
            class="reason-tag"
            :class="{ active: applyForm.reasonCode === r.value }"
            @click="applyForm.reasonCode = r.value"
          >
            {{ r.icon }} {{ $t(r.labelKey) }}
          </span>
        </div>
      </div>

      <!-- 问题描述 -->
      <div class="form-section">
        <div class="form-label">{{ $t('afterSales.problemDesc') }}</div>
        <el-input
          v-model="applyForm.reasonText"
          type="textarea"
          :rows="3"
          :placeholder="$t('afterSales.problemDescPlaceholder')"
          maxlength="500"
          show-word-limit
        ></el-input>
      </div>

      <!-- 上传凭证 -->
      <div class="form-section">
        <div class="form-label">📸 {{ $t('afterSales.uploadEvidence') }}</div>
        <el-upload
          :action="uploadUrl"
          :headers="uploadHeaders"
          list-type="picture-card"
          :file-list="applyForm.evidenceFiles"
          :on-success="handleUploadSuccess"
          :on-remove="handleUploadRemove"
          :limit="9"
          accept="image/*"
        >
          <i class="el-icon-plus"></i>
        </el-upload>
      </div>

      <span slot="footer">
        <el-button @click="showApplyDialog = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" :disabled="!applyForm.reasonCode" @click="submitApply">{{ $t('afterSales.submitApplication') }}</el-button>
      </span>
    </el-dialog>

    <!-- 分页 -->
    <div class="pagination-wrap" v-if="total > pageSize">
      <el-pagination
        background
        layout="prev, pager, next, total"
        :total="total"
        :page-size="pageSize"
        :current-page="currentPage"
        @current-change="handlePageChange"
      />
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import { applyAfterSales } from '@/services/afterSales';
import { getImageUrl } from '@/utils/image';

export default {
  name: 'StallOrders',
  data() {
    return {
      currentStatus: '',
      orders: [],
      loading: false,
      cartCount: 0,
      pendingOrders: 0,
      allOrders: [],
      // 分页
      currentPage: 1,
      pageSize: 20,
      total: 0,
      statusTabs: [
        { labelKey: 'common.all', value: '' },
        { labelKey: 'orderStatus.confirmed', value: 'confirmed' },
        { labelKey: 'orderStatus.readyToShip', value: 'ready_to_ship' },
        { labelKey: 'orderStatus.inTransit', value: 'dispatching' },
        { labelKey: 'orderStatus.completed', value: 'completed' },
        { labelKey: 'orderStatus.cancelled', value: 'cancelled' }
      ],

      showApplyDialog: false,
      currentOrder: null,
      applyForm: {
        reasonCode: '',
        reasonText: '',
        evidenceFiles: []
      },
      reasons: [
        { value: 'DAMAGED', icon: '💔', labelKey: 'afterSales.reasonDefective' },
        { value: 'WRONG_ITEM', icon: '❌', labelKey: 'afterSales.reasonWrongItem' },
        { value: 'NOT_RECEIVED', icon: '📦', labelKey: 'afterSales.reasonNotReceived' },
        { value: 'WRONG_SPEC', icon: '📐', labelKey: 'afterSales.reasonWrongSpec' },
        { value: 'MISSING', icon: '🔍', labelKey: 'afterSales.reasonMissingItem' },
        { value: 'NOT_WANTED', icon: '🙅', labelKey: 'afterSales.reasonDontWant' }
      ]
    };
  },
  created() {
    // 读取 URL 参数，支持多种状态名映射
    const statusParam = this.$route.query.status;
    if (statusParam) {
      // delivering/运输 → dispatching（后端实际状态值）
      const statusMap = {
        'delivering': 'dispatching',
        '运输': 'dispatching',
        '配送': 'dispatching'
      };
      this.currentStatus = statusMap[statusParam] || statusParam;
    }
    this.fetchOrders();
    this.loadCartCount();
  },
  computed: {
    uploadUrl() {
      return `${process.env.VUE_APP_API_URL}/files/upload-image`;
    },
    uploadHeaders() {
      const token = localStorage.getItem('auth_token');
      return token ? { Authorization: `Bearer ${token}` } : {};
    }
  },
  watch: {
    currentStatus() {
      this.applyFilter();
    }
  },
  methods: {
    getImageUrl,
    async fetchOrders() {
      this.loading = true;
      try {
        const customerId = localStorage.getItem('stored_customer_id');
        const response = await api.get('/orders', {
          params: {
            customerId,
            page: this.currentPage,
            size: this.pageSize
          }
        });
        let orders = response?.items || response?.data?.items || [];
        this.total = response?.total || 0;
        // 档口只看自己的订单
        this.allOrders = orders;
        this.applyFilter();
        // 统计待处理订单数
        this.pendingOrders = orders.filter(o => 
          !['completed', 'cancelled'].includes((o.status || '').toLowerCase())
        ).length;
      } catch (error) {
        console.error('加载订单失败:', error);
        this.$message?.error(this.$t('toast.loadFailed'));
      } finally {
        this.loading = false;
      }
    },
    applyFilter() {
      if (this.currentStatus) {
        this.orders = this.allOrders.filter(o => 
          (o.status || '').toLowerCase() === this.currentStatus.toLowerCase()
        );
      } else {
        this.orders = this.allOrders;
      }
    },
    getTabCount(value) {
      if (!value) return this.allOrders.length;
      return this.allOrders.filter(o => 
        (o.status || '').toLowerCase() === value.toLowerCase()
      ).length;
    },
    canPay(order) {
      const unpaidStatuses = ['pending', 'pending_payment', 'confirmed', 'packing', 'ready_to_ship'];
      const status = (order.status || '').toLowerCase();
      // 有支付待审核（后端返回）
      if (order.pendingPaymentAmount > 0) return false;
      // 后端已标记有支付记录
      if (order.hasPaymentRecord) return false;
      return unpaidStatuses.includes(status) && order.totalAmount > 0;
    },
    goToPay(order) {
      this.$router.push(`/stall/orders/${order.id}?action=pay`);
    },
    contactWholesaler(order) {
      if (!order.wholesalerWhatsapp) {
        this.$message?.warning(this.$t('order.noMerchantWhatsapp'));
        return;
      }
      const orderNo = order.orderNo || '';
      const message = `你好，关于订单 ${orderNo}，我想咨询一下...`;
      const url = `https://wa.me/${order.wholesalerWhatsapp}?text=${encodeURIComponent(message)}`;
      window.open(url, '_blank');
    },
    canCancel(order) {
      return order.canCancel === true;
    },
    canAfterSales(order) {
      return order.canAfterSales === true;
    },
    goToOrderAfterSales(order) {
      this.currentOrder = order;
      this.showApplyDialog = true;
    },
    async submitApply() {
      if (!this.applyForm.reasonCode) {
        this.$message.warning('请选择问题原因');
        return;
      }
      try {
        const imageUrls = this.applyForm.evidenceFiles
          .filter(f => f.response && f.response.url)
          .map(f => f.response.url);

        await applyAfterSales({
          orderId: this.currentOrder.id,
          orderNo: this.currentOrder.orderNo,
          reasonCode: this.applyForm.reasonCode,
          reasonText: this.applyForm.reasonText,
          evidenceUrls: JSON.stringify(imageUrls)
        });

        this.$message.success(this.$t('toast.submitSuccess') || this.$t('common.success'));
        this.showApplyDialog = false;
        this.applyForm = { reasonCode: '', reasonText: '', evidenceFiles: [] };
        await this.fetchOrders();
      } catch (e) {
        this.$message.error(this.$t('toast.submitFailed') + ': ' + (e.response?.data?.message || e.message));
      }
    },
    handleUploadSuccess(response, file, fileList) {
      this.applyForm.evidenceFiles = fileList;
    },
    handleUploadRemove(file, fileList) {
      this.applyForm.evidenceFiles = fileList;
    },
    async batchApplyAfterSales() {
      if (this.selectedIds.length === 0) return;
      this.$router.push(`/stall/after-sales`);
    },
    loadCartCount() {
      const cart = JSON.parse(localStorage.getItem('cart') || '[]');
      this.cartCount = cart.length;
    },
    getStatusClass(status) {
      const m = {
        confirmed: 'status-confirmed',
        packing: 'status-packing',
        ready_to_ship: 'status-ready',
        dispatched: 'status-dispatched',
        dispatching: 'status-dispatched',
        delivered: 'status-delivered',
        completed: 'status-completed',
        cancelled: 'status-cancelled'
      };
      return m[status] || 'status-pending';
    },
    getStatusText(status) {
      const m = {
        confirmed: this.$t('orderStatus.confirmed'),
        packing: this.$t('orderStatus.packing'),
        ready_to_ship: this.$t('orderStatus.readyToShip'),
        dispatched: this.$t('orderStatus.dispatched'),
        dispatching: this.$t('orderStatus.inTransit'),
        delivered: this.$t('orderStatus.delivered'),
        completed: this.$t('orderStatus.completed'),
        cancelled: this.$t('orderStatus.cancelled')
      };
      return m[status] || this.$t('orderStatus.pending');
    },
    formatAmount(amount) {
      const n = Number(amount || 0);
      return new Intl.NumberFormat('zh-CN', { minimumFractionDigits: 2 }).format(n);
    },
    formatTime(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const h = String(d.getHours()).padStart(2, '0');
      const min = String(d.getMinutes()).padStart(2, '0');
      const s = String(d.getSeconds()).padStart(2, '0');
      return `${y}-${m}-${day} ${h}:${min}:${s}`;
    },
    viewDetail(order) {
      this.$router.push(`/stall/orders/${order.id}`);
    },
    async cancelOrder(order) {
      try {
        await this.$confirm(`${this.$t('toast.confirmAction')} (${order.orderNo})`, this.$t('common.warning'), {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'warning'
        });
        await api.post(`/orders/${order.id}/cancel`);
        this.$message?.success(this.$t('order.orderCancelled') || this.$t('common.success'));
        await this.fetchOrders();
      } catch (e) {
        if (e !== 'cancel') this.$message?.error('取消失败');
      }
    },
    async confirmPayment(order) {
      try {
        await this.$confirm(this.$t('order.confirmPaymentConfirm'), this.$t('order.confirmPaymentTitle'), {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'warning'
        });
        await api.post(`/orders/${order.id}/confirm-payment`);
        this.$message?.success(this.$t('order.paymentConfirmed'));
        await this.fetchOrders();
      } catch (e) {
        if (e !== 'cancel') this.$message?.error('操作失败');
      }
    },
    navigateTo(tab) {
      const routes = {
        home: '/stall/home',
        products: '/stall/products',
        cart: '/stall/cart',
        orders: '/stall/orders',
        profile: '/stall/profile'
      };
      const targetRoute = routes[tab];
      if (targetRoute && this.$route.path !== targetRoute) {
        this.$router.push(targetRoute).catch(err => {
          if (err.name !== 'NavigationDuplicated') console.error(err);
        });
      }
    },
    goToAfterSales() {
      this.$router.push('/stall/after-sales');
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.fetchOrders();
    }
  }
};
</script>

<style scoped>
.stall-orders {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 120px;
}

/* 分页 */
.pagination-wrap {
  display: flex;
  justify-content: center;
  padding: 20px 16px;
  background: #fff;
  margin-top: 12px;
}

.header {
  background: white;
  border-bottom: 1px solid #eee;
  padding: calc(12px + env(safe-area-inset-top, 0px)) 16px;
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-center {
  flex: 1;
  text-align: center;
  font-weight: bold;
  color: #333;
  font-size: 16px;
}

.btn-icon {
  font-size: 20px;
  cursor: pointer;
  background: none;
  border: none;
  color: #333;
}

/* 筛选标签 */
.filter-tabs {
  background: white;
  display: flex;
  padding: 8px 0;
  overflow-x: auto;
  border-bottom: 1px solid #eee;
}

.filter-tab {
  flex: 1;
  text-align: center;
  padding: 8px 12px;
  font-size: 13px;
  color: #666;
  cursor: pointer;
  white-space: nowrap;
  border-bottom: 2px solid transparent;
}

.filter-tab.active {
  color: #667eea;
  border-bottom-color: #667eea;
  font-weight: 600;
}

/* 内容区 */
.content {
  padding: 12px;
}

.loading-state, .empty-state {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 12px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.empty-state .btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  margin-top: 12px;
  height: auto;
  line-height: normal;
}

/* 订单卡片 */
.order-card {
  background: white;
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  cursor: pointer;
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.order-no {
  font-size: 13px;
  color: #666;
  font-family: monospace;
}

.order-status {
  font-size: 12px;
  padding: 2px 10px;
  border-radius: 10px;
  font-weight: 500;
}

.status-confirmed { background: #e6f7ff; color: #1890ff; }
.status-packing { background: #e6f7ff; color: #1890ff; }
.status-ready { background: #f6ffed; color: #52c41a; }
.status-dispatched { background: #f6ffed; color: #52c41a; }
.status-delivered { background: #f0f5ff; color: #597ef7; }
.status-completed { background: #f0f5ff; color: #597ef7; }
.status-cancelled { background: #fff1f0; color: #f5222d; }
.status-pending { background: #fff7e6; color: #fa8c16; }

.order-items {
  margin-bottom: 8px;
}

.order-item {
  display: flex;
  align-items: center;
  padding: 6px 0;
  gap: 10px;
}

.item-image {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 6px;
  overflow: hidden;
  background: #f5f5f5;
}

.item-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.item-img-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.item-info {
  flex: 1;
  min-width: 0;
}

.item-name {
  font-size: 13px;
  color: #333;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-qty {
  font-size: 12px;
  color: #999;
}

.order-footer {
  display: flex;
  justify-content: space-between;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
  font-size: 12px;
}

.order-time { color: #999; }
.order-total { color: #333; font-weight: 600; }
.order-paid {
  font-size: 12px;
  color: #10b981;
  font-weight: 600;
  margin-top: 6px;
}
.order-pending-pay {
  font-size: 12px;
  color: #f59e0b;
  font-weight: 600;
  margin-top: 6px;
}
.order-after-sales {
  margin-top: 6px;
}

.order-actions {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}

.order-actions button {
  flex: 1;
  padding: 0;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  text-align: center;
  height: 40px;
  line-height: 40px;
  white-space: nowrap;
  box-sizing: border-box;
}

.btn-link {
  background: none;
  border: none;
  color: #667eea;
  cursor: pointer;
  font-size: 12px;
  padding: 0;
}
.btn-link:hover { text-decoration: underline; }

.btn-secondary {
  background: #f0f0f0;
  color: #333;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-danger {
  background: #fff5f5;
  color: #f5222d;
}

.btn-warning {
  background: #fff7ed;
  color: #c2410c;
}

.btn-whatsapp {
  background: #dcfce7;
  color: #16a34a;
}

.btn-whatsapp:disabled {
  background: #f3f4f6;
  color: #9ca3af;
  cursor: not-allowed;
}

/* 底部导航 */
.bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: space-around;
  z-index: 100;
  height: 56px;
}

.nav-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #999;
  font-size: 11px;
  border: none;
  background: none;
  transition: color 0.3s;
  position: relative;
}

.nav-item.active {
  color: #667eea;
}

.nav-item-icon {
  font-size: 24px;
  margin-bottom: 2px;
}

.cart-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  background: #f5576c;
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: bold;
}

/* 申请售后弹窗 */
.apply-info {
  background: #f0f7ff;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  color: #457b9d;
  margin-bottom: 16px;
}
.form-section { margin-bottom: 16px; }
.form-label { font-size: 13px; font-weight: 600; margin-bottom: 8px; }
.reason-tags { display: flex; flex-wrap: wrap; gap: 6px; }
.reason-tag {
  padding: 6px 10px;
  border: 1px solid #e8e8e8;
  border-radius: 16px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}
.reason-tag.active { border-color: #1890ff; background: #e6f7ff; color: #1890ff; }
</style>
